/**
 * Live Behavioral Signal Ingestion Engine
 * ---------------------------------------------------------------
 * Converts platform telemetry (TikTok / YouTube Shorts / Instagram
 * Reels) into behavioral learning signals.
 *
 * Mock ingestion is supported today; the same surface area will
 * accept real API payloads in the future.
 */
import { supabase } from "@/integrations/supabase/client";

export const LIVE_TELEMETRY_EVENT = "greyrock:live-telemetry-ingested";

export type IngestPlatform = "tiktok" | "youtube" | "instagram";

export interface PlatformSignaturePreset {
  replay: [number, number];
  completion: [number, number];
  save: [number, number];
  share: [number, number];
  comment: [number, number];
  // Behavioral bias the platform currently rewards
  bias: { dimension: "format" | "archetype" | "escalation_path" | "pacing"; key: string; direction: "increase" | "decrease" };
}

export const PLATFORM_PRESETS: Record<IngestPlatform, PlatformSignaturePreset> = {
  tiktok: {
    replay: [55, 92],
    completion: [28, 58],
    save: [2.0, 6.5],
    share: [1.4, 5.8],
    comment: [1.2, 4.4],
    bias: { dimension: "escalation_path", key: "guilt", direction: "increase" },
  },
  youtube: {
    replay: [18, 44],
    completion: [55, 88],
    save: [1.0, 3.5],
    share: [0.8, 2.6],
    comment: [0.6, 2.2],
    bias: { dimension: "pacing", key: "slow_burn", direction: "increase" },
  },
  instagram: {
    replay: [38, 70],
    completion: [40, 72],
    save: [3.0, 8.0],
    share: [2.0, 6.0],
    comment: [0.9, 3.0],
    bias: { dimension: "format", key: "ambiguity_hook", direction: "increase" },
  },
};

const rand = (min: number, max: number) => min + Math.random() * (max - min);
const pick = <T,>(arr: T[]) => arr[Math.floor(Math.random() * arr.length)];

export interface IngestRow {
  platform: IngestPlatform;
  external_post_id: string;
  asset_id: string | null;
  hook: string | null;
  format: string | null;
  archetype: string | null;
  escalation_path: string | null;
  pacing: string | null;
  replay_rate: number;
  completion_rate: number;
  save_rate: number;
  share_rate: number;
  comment_velocity: number;
  engagement_score: number;
  virality_score: number;
  predicted_virality: number | null;
  prediction_accuracy: number | null;
  source: "mock" | "tiktok_api" | "youtube_api" | "instagram_api";
  collected_at: string;
}

interface IngestOptions {
  count: number;
  platforms?: IngestPlatform[];
  source?: IngestRow["source"];
}

/**
 * Pull recent assets (with predictions if any) and synthesize
 * platform telemetry rows that look like real ingestion payloads.
 */
export async function generateMockIngestion({
  count,
  platforms = ["tiktok", "youtube", "instagram"],
  source = "mock",
}: IngestOptions): Promise<IngestRow[]> {
  const { data: assets } = await (supabase as any)
    .from("content_assets")
    .select("id, hook, format, archetype, escalation_path, behavioral_intensity, platform")
    .order("created_at", { ascending: false })
    .limit(400);

  const list = (assets ?? []) as any[];
  if (list.length === 0) return [];

  const ids = list.map((a) => a.id);
  const { data: preds } = await (supabase as any)
    .from("asset_predictions")
    .select("asset_id, predicted_virality_score")
    .in("asset_id", ids);

  const predMap = new Map<string, number>();
  (preds ?? []).forEach((p: any) => {
    if (p.asset_id) predMap.set(p.asset_id, Number(p.predicted_virality_score) || 0);
  });

  const rows: IngestRow[] = [];
  for (let i = 0; i < count; i++) {
    const asset = pick(list);
    const platform = pick(platforms);
    const sig = PLATFORM_PRESETS[platform];

    const replay = +rand(...sig.replay).toFixed(2);
    const completion = +rand(...sig.completion).toFixed(2);
    const save = +rand(...sig.save).toFixed(2);
    const share = +rand(...sig.share).toFixed(2);
    const comment = +rand(...sig.comment).toFixed(2);

    const engagement = Math.min(100, replay * 0.35 + completion * 0.25 + save * 4 + share * 3 + comment * 2);
    const virality = Math.min(99, Math.max(5, replay * 0.32 + completion * 0.22 + save * 3.5 + share * 4));

    const predicted = predMap.get(asset.id) ?? null;
    const accuracy = predicted != null && predicted > 0
      ? +(100 - Math.min(100, Math.abs(predicted - virality))).toFixed(1)
      : null;

    rows.push({
      platform,
      external_post_id: `${platform.slice(0, 2)}_${Math.random().toString(36).slice(2, 10)}`,
      asset_id: asset.id,
      hook: asset.hook ?? null,
      format: asset.format ?? null,
      archetype: asset.archetype ?? null,
      escalation_path: asset.escalation_path ?? null,
      pacing: asset.behavioral_intensity ?? null,
      replay_rate: replay,
      completion_rate: completion,
      save_rate: save,
      share_rate: share,
      comment_velocity: comment,
      engagement_score: +engagement.toFixed(2),
      virality_score: +virality.toFixed(2),
      predicted_virality: predicted,
      prediction_accuracy: accuracy,
      source,
      collected_at: new Date().toISOString(),
    });
  }
  return rows;
}

/** Persist telemetry, then derive drift + reality corrections. */
export async function ingestTelemetry(rows: IngestRow[]): Promise<{
  inserted: number;
  driftEvents: number;
  corrections: number;
}> {
  if (rows.length === 0) return { inserted: 0, driftEvents: 0, corrections: 0 };

  const { error } = await (supabase as any).from("platform_signal_ingestion").insert(rows);
  if (error) throw error;

  const drift = await detectDrift(rows);
  const corrections = await applyRealityCorrections(rows);

  window.dispatchEvent(new CustomEvent(LIVE_TELEMETRY_EVENT, { detail: { count: rows.length } }));

  return { inserted: rows.length, driftEvents: drift, corrections };
}

/**
 * Detect behavioral drift by comparing fresh batch averages per
 * (platform, archetype) against the recent baseline window.
 */
async function detectDrift(rows: IngestRow[]): Promise<number> {
  const { data: baseline } = await (supabase as any)
    .from("platform_signal_ingestion")
    .select("platform, archetype, virality_score, replay_rate, save_rate")
    .order("collected_at", { ascending: false })
    .limit(2000);

  const baseMap = new Map<string, { virality: number[]; replay: number[]; save: number[] }>();
  (baseline ?? []).forEach((b: any) => {
    if (!b.archetype) return;
    const k = `${b.platform}::${b.archetype}`;
    if (!baseMap.has(k)) baseMap.set(k, { virality: [], replay: [], save: [] });
    const r = baseMap.get(k)!;
    r.virality.push(Number(b.virality_score) || 0);
    r.replay.push(Number(b.replay_rate) || 0);
    r.save.push(Number(b.save_rate) || 0);
  });

  const incoming = new Map<string, { virality: number[]; replay: number[]; save: number[]; sample: number }>();
  rows.forEach((r) => {
    if (!r.archetype) return;
    const k = `${r.platform}::${r.archetype}`;
    if (!incoming.has(k)) incoming.set(k, { virality: [], replay: [], save: [], sample: 0 });
    const x = incoming.get(k)!;
    x.virality.push(r.virality_score);
    x.replay.push(r.replay_rate);
    x.save.push(r.save_rate);
    x.sample += 1;
  });

  const avg = (a: number[]) => (a.length ? a.reduce((s, n) => s + n, 0) / a.length : 0);
  const driftRows: any[] = [];

  incoming.forEach((freshVal, k) => {
    const base = baseMap.get(k);
    if (!base) return;
    const [platform, archetype] = k.split("::");
    const fresh = freshVal;

    const dV = avg(fresh.virality) - avg(base.virality);
    const dR = avg(fresh.replay) - avg(base.replay);
    const dS = avg(fresh.save) - avg(base.save);

    const candidates: Array<{ pattern: string; delta: number }> = [
      { pattern: `${archetype} virality drift`, delta: dV },
      { pattern: `${archetype} replay drift`, delta: dR },
      { pattern: `${archetype} save velocity drift`, delta: dS },
    ];

    candidates.forEach((c) => {
      if (Math.abs(c.delta) < 4) return; // noise floor
      const direction = c.delta > 0 ? "rising" : "declining";
      const strength = Math.min(100, Math.abs(c.delta) * 4);
      const confidence = Math.min(99, 50 + Math.abs(c.delta) * 2 + Math.min(20, fresh.sample));
      driftRows.push({
        platform,
        behavioral_pattern: c.pattern,
        trend_direction: direction,
        confidence: +confidence.toFixed(1),
        signal_strength: +strength.toFixed(1),
        delta: +c.delta.toFixed(2),
        sample_size: fresh.sample,
        metadata: { archetype },
      });
    });
  });

  if (driftRows.length > 0) {
    await (supabase as any).from("behavioral_platform_drift").insert(driftRows);
  }
  return driftRows.length;
}

/**
 * Reality correction: when actual virality diverges from synthetic
 * predictions, push offsetting weight into adaptive_learning_profiles
 * so future generation biases reflect platform truth.
 */
async function applyRealityCorrections(rows: IngestRow[]): Promise<number> {
  const corrections = new Map<string, { weightDelta: number; samples: number; key: string; dimension: string; meta: any }>();

  rows.forEach((r) => {
    if (r.predicted_virality == null || !r.archetype) return;
    const diff = r.virality_score - r.predicted_virality;
    if (Math.abs(diff) < 5) return;
    const sign = diff > 0 ? 1 : -1;
    const magnitude = Math.min(0.25, Math.abs(diff) / 100);

    const dims: Array<{ dimension: string; key: string }> = [
      { dimension: "archetype", key: r.archetype },
      ...(r.format ? [{ dimension: "format", key: r.format }] : []),
      ...(r.escalation_path ? [{ dimension: "escalation_path", key: r.escalation_path }] : []),
    ];

    dims.forEach(({ dimension, key }) => {
      const id = `${dimension}::${key}`;
      if (!corrections.has(id)) {
        corrections.set(id, { weightDelta: 0, samples: 0, key, dimension, meta: { platform: r.platform } });
      }
      const c = corrections.get(id)!;
      c.weightDelta += sign * magnitude;
      c.samples += 1;
    });
  });

  if (corrections.size === 0) return 0;

  const ids = Array.from(corrections.values());
  const { data: existing } = await (supabase as any)
    .from("adaptive_learning_profiles")
    .select("id, dimension, key, weight, sample_size, metadata")
    .in("dimension", Array.from(new Set(ids.map((c) => c.dimension))));

  const existingMap = new Map<string, any>();
  (existing ?? []).forEach((e: any) => {
    existingMap.set(`${e.dimension}::${e.key}`, e);
  });

  const upserts: any[] = [];
  corrections.forEach((c, k) => {
    const ex = existingMap.get(k);
    const baseWeight = ex?.weight ?? 1;
    const newWeight = Math.max(0.1, Math.min(3, baseWeight + c.weightDelta / Math.max(1, c.samples)));
    upserts.push({
      ...(ex?.id ? { id: ex.id } : {}),
      dimension: c.dimension,
      key: c.key,
      weight: +newWeight.toFixed(3),
      rolling_score: ex?.rolling_score ?? 0,
      trend_delta: +(c.weightDelta / Math.max(1, c.samples)).toFixed(3),
      sample_size: (ex?.sample_size ?? 0) + c.samples,
      metadata: { ...(ex?.metadata ?? {}), reality_correction: true, last_source: "platform_telemetry", platform: c.meta.platform },
    });
  });

  // Upsert one-by-one (no unique constraint guaranteed across env)
  for (const row of upserts) {
    if (row.id) {
      await (supabase as any).from("adaptive_learning_profiles").update(row).eq("id", row.id);
    } else {
      await (supabase as any).from("adaptive_learning_profiles").insert(row);
    }
  }

  return upserts.length;
}
